import { createContext, useContext, useState, useCallback } from 'react';

interface Error {
  id: string;
  message: string;
  type: 'error' | 'warning' | 'info';
}

interface ErrorContextType {
  errors: Error[];
  addError: (message: string, type?: 'error' | 'warning' | 'info') => void;
  removeError: (id: string) => void;
  clearErrors: () => void;
}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

export function ErrorProvider({ children }: { children: React.ReactNode }) {
  const [errors, setErrors] = useState<Error[]>([]);

  const addError = useCallback((message: string, type: 'error' | 'warning' | 'info' = 'error') => {
    const id = Math.random().toString(36).substr(2, 9);
    setErrors(prev => [...prev, { id, message, type }]);

    // Automatisches Entfernen nach 5 Sekunden
    setTimeout(() => {
      removeError(id);
    }, 5000);
  }, []);

  const removeError = useCallback((id: string) => {
    setErrors(prev => prev.filter(error => error.id !== id));
  }, []);

  const clearErrors = useCallback(() => {
    setErrors([]);
  }, []);

  return (
    <ErrorContext.Provider value={{ errors, addError, removeError, clearErrors }}>
      {children}
    </ErrorContext.Provider>
  );
}

export function useError() {
  const context = useContext(ErrorContext);
  if (context === undefined) {
    throw new Error('useError muss innerhalb eines ErrorProviders verwendet werden');
  }
  return context;
} 